/**
 * PatientTagsAdmin.tsx — Admin > Patient Tags.
 *
 * The corporate dashboard's Patient Tags page for the signed-in account: the
 * account's custom tags (tags_custom), in the Lead Sources layout (see
 * components/admin/AdminList.tsx):
 *   - Tag (its icon and title) and Options (corp's outline badges: Admin add,
 *     Admin remove, Clickable), by title; search by title or icon; Show
 *     deleted;
 *   - "Add tag" top right; a click on a live row opens it; Edit / Delete on a
 *     live row (corp's warning first), Restore on a deleted one;
 *   - corp's form: Title *, Icon (the shared icon library), Permissions
 *     (Added / Removed by admin only) and Clickable URL, with corp's
 *     suggestions (no icon; admin-only; a live tag of the same name).
 * Delete sets the tag inactive (it stops showing on patients); Restore sets it
 * active again.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live tag still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { DialogField, LABEL_CLASS, SwitchField, fieldClasses } from '@/components/admin/DialogChrome';
import { TagIconImage, TagIconPicker } from '@/components/admin/TagIconPicker';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  DEFAULT_TAG_ICON,
  createPatientTag,
  deletePatientTag,
  listAdminPatientTags,
  restorePatientTag,
  updatePatientTag,
  type AdminPatientTag,
} from '@/services/patientTags.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Tag', sortable: true },
  { key: 'options', header: 'Options' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function tagTitle(t: AdminPatientTag): string {
  return t.title.trim() || `Tag #${t.tagsCustomID}`;
}

function sameTitle(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function PatientTagsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [tags, setTags] = useState<AdminPatientTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminPatientTag | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminPatientTag | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setTags(await listAdminPatientTags());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load patient tags'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      tags
        .filter(
          (t) =>
            (showDeleted || t.active) &&
            (query === '' || t.title.toLowerCase().includes(query) || t.icon.toLowerCase().includes(query)),
        )
        .sort((a, b) => compareText(tagTitle(a), tagTitle(b)) || a.tagsCustomID - b.tagsCustomID),
    [tags, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'title', compare: { title: (a, b) => compareText(tagTitle(a), tagTitle(b)) } });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { tagsCustomID } = deleting;
    try {
      await deletePatientTag(tagsCustomID);
      setTags((prev) => prev.map((t) => (t.tagsCustomID === tagsCustomID ? { ...t, active: false } : t)));
      toast.success('Tag deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The tag could not be deleted.'));
    }
  }

  async function handleRestore(t: AdminPatientTag) {
    try {
      await restorePatientTag(t.tagsCustomID);
      setTags((prev) => prev.map((x) => (x.tagsCustomID === t.tagsCustomID ? { ...x, active: true } : x)));
      toast.success('Tag restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The tag could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Patient Tags"
      subtitle="Tags staff can put on patients, with their icons and who may add or remove them."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add tag
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search tags..."
          searchLabel="Search tags"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="patient-tags-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading patient tags…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No tags found' : 'No tags yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a tag to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Patient tags" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const title = tagTitle(t);
              const options = [t.adminAdd && 'Admin add', t.adminRemove && 'Admin remove'].filter(Boolean) as string[];
              return (
                <tr
                  key={t.tagsCustomID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(t)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <div className="flex items-center gap-2">
                      <TagIconImage path={t.icon || null} size={20} />
                      <AdminRowName deleted={deleted} nameTestId="patient-tag-title">
                        {title}
                      </AdminRowName>
                    </div>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {options.length === 0 && !t.urlTarget ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {options.map((o) => (
                          <OutlineBadge key={o}>{o}</OutlineBadge>
                        ))}
                        {t.urlTarget && <OutlineBadge tone="blue">Clickable</OutlineBadge>}
                      </div>
                    )}
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(t)} />
                            <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(t)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['tag', 'tags']} />
        </>
      )}

      {editing && (
        <PatientTagFormModal
          key={editing === 'new' ? 'new' : editing.tagsCustomID}
          tag={editing === 'new' ? null : editing}
          allTags={tags}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${tagTitle(deleting)}"?` : ''}
        message="It will stop appearing on patients in this account. You can restore it from Show deleted."
        confirmLabel="Delete tag"
        variant="danger"
      />
    </AdminListPage>
  );
}

function PatientTagFormModal({
  tag,
  allTags,
  onClose,
  onSaved,
}: {
  tag: AdminPatientTag | null;
  allTags: AdminPatientTag[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = tag === null;
  const t = useTrackedForm(
    () => ({
      title: tag?.title ?? '',
      icon: tag ? tag.icon : DEFAULT_TAG_ICON,
      adminAdd: tag?.adminAdd ?? false,
      adminRemove: tag?.adminRemove ?? false,
      urlTarget: tag?.urlTarget ?? false,
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missingTitle = t.form.title.trim() === '';
  const noIcon = t.form.icon.trim() === '';

  const suggestions = [
    ...(noIcon ? ['No icon selected — the tag will show a placeholder wherever it appears'] : []),
    ...(!missingTitle &&
    allTags.some((x) => x.active && x.tagsCustomID !== tag?.tagsCustomID && sameTitle(x.title, t.form.title))
      ? [`A tag named "${t.form.title.trim()}" already exists`]
      : []),
    ...(t.form.adminAdd && t.form.adminRemove ? ['Only admins will be able to add or remove this tag'] : []),
  ];

  async function save() {
    if (missingTitle) return;
    setSaving(true);
    try {
      const data = {
        title: t.form.title.trim(),
        icon: t.form.icon,
        adminAdd: t.form.adminAdd,
        adminRemove: t.form.adminRemove,
        urlTarget: t.form.urlTarget,
      };
      if (tag) await updatePatientTag(tag.tagsCustomID, data);
      else await createPatientTag(data);
      toast.success(isNew ? 'Tag created' : 'Tag updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The tag could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Patient Tag' : 'Edit Patient Tag'}
      isNew={isNew}
      dirty={isNew ? t.form.title !== '' || t.changed.size > 0 : t.dirty}
      saving={saving}
      errors={missingTitle ? ['Title is required'] : []}
      suggestions={suggestions}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="patient-tag-title"
          required
          state={missingTitle ? 'error' : 'none'}
          labelHint={missingTitle ? '— required' : undefined}
          badge={t.badge('title', 'Title')}
        >
          <Input
            id="patient-tag-title"
            value={t.form.title}
            placeholder="Tag title"
            maxLength={255}
            onChange={(e) => t.patch({ title: e.target.value })}
            className={fieldClasses.input(missingTitle ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField
          label="Icon"
          htmlFor="patient-tag-icon"
          state={noIcon ? 'warning' : 'none'}
          labelHint={noIcon ? '— suggested' : undefined}
          badge={t.badge('icon', 'Icon')}
        >
          <TagIconPicker id="patient-tag-icon" value={t.form.icon} onChange={(icon) => t.patch({ icon })} warn={noIcon} />
        </DialogField>
        <div className="space-y-3">
          <span className={LABEL_CLASS}>
            <span>Permissions</span>
          </span>
          <SwitchField
            id="patient-tag-admin-add"
            label="Added by admin only"
            checked={t.form.adminAdd}
            onChange={(v) => t.patch({ adminAdd: v })}
            badge={t.badge('adminAdd', 'Added by admin only')}
          />
          <SwitchField
            id="patient-tag-admin-remove"
            label="Removed by admin only"
            checked={t.form.adminRemove}
            onChange={(v) => t.patch({ adminRemove: v })}
            badge={t.badge('adminRemove', 'Removed by admin only')}
          />
        </div>
        <SwitchField
          id="patient-tag-url-target"
          label="Clickable URL"
          checked={t.form.urlTarget}
          onChange={(v) => t.patch({ urlTarget: v })}
          badge={t.badge('urlTarget', 'Clickable URL')}
          description="When on, staff can store a link against this tag per patient, and the tag icon opens it in a new tab. The link itself lives on the patient, not here."
        />
      </div>
    </AdminFormModal>
  );
}
